import { roundTo2Decimals } from '../../../utils/numberFormat';

export function formatINR(num) {
  if (isNaN(num)) return '';
  return Number(num).toLocaleString('en-IN');
}

export function getLineDiscountAmount(item = {}) {
  const qty = parseFloat(item.qty) || 0;
  const rate = parseFloat(item.rate) || 0;
  const rawAmount = qty * rate;
  let lineDisc = 0;
  if ((item.lineDiscountType || 'amount') === 'percent') {
    lineDisc = rawAmount * ((parseFloat(item.lineDiscountValue) || 0) / 100);
  } else {
    lineDisc = parseFloat(item.lineDiscountValue) || 0;
  }
  return Math.min(Math.max(lineDisc, 0), rawAmount);
}

/**
 * Invoice line `amount` is already net of per-line discounts.
 * Legacy bills stored an overall discountType/discountValue instead.
 * Line discounts win when present so the summary can show the total
 * without subtracting it again from the net subtotal.
 */
export function calculateInvoiceDiscount(items = [], billData = {}) {
  const netSubtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || 0;
  const lineDiscountTotal = items.reduce((sum, item) => sum + getLineDiscountAmount(item), 0);
  const hasLineDiscount = items.some((item) => parseFloat(item.lineDiscountValue) > 0);
  const billDiscountValue = parseFloat(billData.discountValue);
  const hasBillDiscount = Boolean(billData.discountType) && Number.isFinite(billDiscountValue) && billDiscountValue > 0;

  if (hasLineDiscount) {
    const discount = roundTo2Decimals(lineDiscountTotal);
    return {
      subtotal: netSubtotal,
      discount,
      discountedSubtotal: netSubtotal,
      discountRatio: 0,
      discountPercentLabel: '',
      grossAmount: roundTo2Decimals(netSubtotal + discount),
    };
  }

  if (hasBillDiscount) {
    let discount = 0;
    if (billData.discountType === 'percent') {
      discount = netSubtotal * (billDiscountValue / 100);
    } else {
      discount = billDiscountValue;
    }
    discount = roundTo2Decimals(Math.min(Math.max(discount, 0), netSubtotal));
    return {
      subtotal: netSubtotal,
      discount,
      discountedSubtotal: Math.max(0, roundTo2Decimals(netSubtotal - discount)),
      discountRatio: netSubtotal > 0 ? discount / netSubtotal : 0,
      discountPercentLabel: billData.discountType === 'percent' ? `(${billData.discountValue || 0}%)` : '',
      grossAmount: netSubtotal,
    };
  }

  return {
    subtotal: netSubtotal,
    discount: 0,
    discountedSubtotal: netSubtotal,
    discountRatio: 0,
    discountPercentLabel: '',
    grossAmount: netSubtotal,
  };
}

function numberToWordsIndian(num) {
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ` ${a[n % 10]}` : '');
    if (n < 1000) return `${a[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${inWords(n % 100)}` : ''}`;
    if (n < 100000) return `${inWords(Math.floor(n / 1000))} Thousand${n % 1000 ? ` ${inWords(n % 1000)}` : ''}`;
    if (n < 10000000) return `${inWords(Math.floor(n / 100000))} Lakh${n % 100000 ? ` ${inWords(n % 100000)}` : ''}`;
    return `${inWords(Math.floor(n / 10000000))} Crore${n % 10000000 ? ` ${inWords(n % 10000000)}` : ''}`;
  }

  return inWords(Math.floor(num));
}

export function numToWords(num) {
  return `${numberToWordsIndian(num)} Rupees Only`;
}
